import { createElement, ReactElement } from "react";
import { observable } from "mobx";

import { animate } from "@formant/ui/utils/animate";

import { track } from "../analytics";
import { NotFound } from "../component/NotFound";
import { ILogger } from "../context/LoggerContext/ILogger";
import { convertRoutesToGenerator } from "../context/RouterContext/ConvertRoutesToGenerator";
import { RouteGenerators } from "../context/RouterContext/RouteGenerators";
import { INavigateOptions } from "../router/INavigateOptions";
import { IRoute, RouteProps } from "../router/IRoute";
import { IRoutes } from "../router/IRoutes";
import { clearHashState, updateHash } from "../utils/hashObservable";
import { IOperateStore } from "./IOperateStore";
import { IRouterStore } from "./IRouterStore";

const emptyRoutes = new Proxy(
    {},
    {
        get(_, prop): IRoute {
            throw new Error(`Cannot get route ${String(prop)}: Routes not set`);
        },
    },
) as IRoutes;

export class RouterStore implements IRouterStore {
    @observable
    private _page: ReactElement | undefined;

    @observable
    private _hasHistory: boolean = false;

    private _routes: IRoutes = emptyRoutes;
    private readonly routeHistory: {
        path: string;
        route: IRoute<RouteProps>;
    }[] = [];
    private routeChangeHandlers: Array<() => void> = [];

    private _generators: RouteGenerators =
        convertRoutesToGenerator(emptyRoutes);

    constructor(
        private readonly operateStore: IOperateStore,
        private readonly logger: ILogger,
    ) {}

    public get page() {
        return this._page;
    }

    public get hasHistory() {
        return this._hasHistory;
    }

    public get routeTo(): RouteGenerators {
        return this._generators;
    }

    public setRoutes(routes: IRoutes) {
        if (this._routes === emptyRoutes) {
            this._routes = routes;
            this._generators = convertRoutesToGenerator(routes);
        } else {
            this.logger.warn("Cannot reset routes");
        }
    }

    navigate = async (
        to?: string,
        options: INavigateOptions = {},
    ): Promise<void> => {
        this.operateStore.session?.setDisconnectIntent();

        const { replace = false, hasHistory = true } = options;

        if (to) {
            clearHashState();
            const { history } = window;
            history[replace ? "replaceState" : "pushState"](
                {},
                document?.title || "",
                to,
            );
            this._hasHistory = hasHistory;
            updateHash();
        }

        const path = to || window.location.pathname;

        let match: [string, ReactElement, IRoute<RouteProps>] | undefined;
        const allRoutes = Object.entries(this._routes).filter(
            (it: [string, IRoute | undefined]): it is [string, IRoute] =>
                it[1] !== undefined,
        );
        for (const [name, route] of allRoutes) {
            const p = route.match(path);
            if (p) {
                match = [name, p, route];
                break;
            }
        }

        const [routeName, page, route] = match ?? [];
        if (page && route) {
            if (replace) {
                this.routeHistory.pop();
            }
            this.routeHistory.push({ path, route });
            while (this.routeHistory.length > 5) {
                this.routeHistory.shift();
            }
        }

        if (path) {
            this.logger.info(`Navigate: ${path} [${routeName ?? "unknown"}]`);
            track("Navigate", { Path: path });
        }

        this._page = page ?? createElement(NotFound);

        this.notifyRouteChangeHandlers();

        await animate();
    };

    registerRouteChangeHandler = (handler: () => void): void => {
        this.routeChangeHandlers.push(handler);
    };

    unregisterRouteChangeHandler = (handler: () => void): void => {
        this.routeChangeHandlers = this.routeChangeHandlers.filter(
            h => h !== handler,
        );
    };

    private notifyRouteChangeHandlers = (): void => {
        this.routeChangeHandlers.forEach(handler => {
            try {
                handler();
            } catch (error) {
                this.logger.error("Error in route change handler", { error });
            }
        });
    };

    back = async () => {
        if (this._hasHistory) {
            window.history.back();
        } else {
            await this.navigate("/");
        }
    };

    wasViewingDeviceId():
        | { id: string; viewId: string }
        | { id: string }
        | false {
        if (this.routeHistory.length <= 1) {
            return false;
        }

        const { _routes: routes } = this;
        const previous = this.routeHistory[this.routeHistory.length - 2];
        if (previous.route === routes.unifiedDevice) {
            const params = previous.route.extractParameters(previous.path);
            if (params && params.id) {
                return { id: params.id, viewId: params.viewId };
            }
        } else if (previous.route === routes.device) {
            const params = previous.route.extractParameters(previous.path);
            if (params && params.id) {
                return { id: params.id };
            }
        }

        return false;
    }

    wasViewingTeleopId(): string | false {
        if (this.routeHistory.length <= 1) {
            return false;
        }

        const { _routes: routes } = this;
        const previous = this.routeHistory[this.routeHistory.length - 2];
        if (
            previous.route === routes.teleop ||
            previous.route === routes.teleopOnboarding
        ) {
            const params = previous.route.extractParameters(previous.path);
            if (params && params.id) {
                return params.id;
            }
        }

        return false;
    }

    wasViewingOverview(): boolean {
        if (this.routeHistory.length <= 1) {
            return false;
        }

        const { _routes: routes } = this;
        const previous = this.routeHistory[this.routeHistory.length - 2];
        if (previous.route === routes.home) {
            return true;
        }

        return false;
    }
}
